import { Box, FileUpload, IconButton, Loader, Text, TextButton, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Attachment } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AttachingMethod, TLocalesKeys as K } from '../../../../../types';
import { SUPPORTED_IMAGE_FILES, SUPPORTED_VIDEO_FILES } from '../../../../common/api/uploadMedia';
import { selectAttaches } from '../../../../common/selectors/attaches';
import { useAppDispatch } from '../../../../common/store';
import { loadAttaches } from '../../../../common/thunks/attaches.thunk';
import { renderModel } from '../../../../util/renderModel';
import { attachFileDataHooks } from './AttachFile.dataHooks';

const { locale } = renderModel.get();

const FILE_UPLOAD_ACCEPT = [...SUPPORTED_VIDEO_FILES, ...SUPPORTED_IMAGE_FILES]
  .map((subtype) => `.${subtype}`)
  .join(',');

export const AttachFile: FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading } = useSelector(selectAttaches);
  const { t } = useTranslation();

  const changeHandler = useCallback(
    (fileList: FileList) => {
      const files = Array.from(fileList);

      dispatch(loadAttaches({ files, attachingMethod: AttachingMethod.Button }));
    },
    [dispatch],
  );

  const tooltipContent = useMemo(
    () => (
      <Box direction="vertical">
        <Text size="small" weight="thin" light>
          {t<K>('attach_file_popup_text')}
        </Text>
        <TextButton
          skin="light"
          size="small"
          as="a"
          target="_blank"
          href={`https://support.wix.com/${locale}/article/wix-media-supported-media-file-types-and-file-sizes`}
          underline="always"
          dataHook={attachFileDataHooks.tooltipArticleLink()}
        >
          {t<K>('attach_file_popup_link')}
        </TextButton>
      </Box>
    ),
    [t],
  );

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent): void => {
      if (event.clipboardData?.files.length) {
        const files = Array.from(event.clipboardData.files);

        dispatch(loadAttaches({ files, attachingMethod: AttachingMethod.Paste }));
      }
    };

    window.addEventListener('paste', handlePaste);

    return () => window.removeEventListener('paste', handlePaste);
  }, [dispatch]);

  return (
    <Box verticalAlign="middle">
      {isLoading ? (
        // Attachment icon is 24px wide, while the tiny loader is 18px. So compensating the width
        // and adding center alignment
        <Box width="30px" align="center">
          <Loader size="tiny" dataHook={attachFileDataHooks.loader()} />
        </Box>
      ) : (
        <Tooltip maxWidth={391} content={tooltipContent} dataHook={attachFileDataHooks.tooltip()}>
          <FileUpload
            multiple
            capture="none"
            accept={FILE_UPLOAD_ACCEPT}
            onChange={changeHandler}
            dataHook={attachFileDataHooks.fileUpload()}
          >
            {({ openFileUploadDialog }) => (
              <Box verticalAlign="middle">
                <IconButton
                  skin="light"
                  size="small"
                  priority="primary"
                  onClick={openFileUploadDialog}
                  dataHook={attachFileDataHooks.button()}
                >
                  <Attachment />
                </IconButton>
              </Box>
            )}
          </FileUpload>
        </Tooltip>
      )}
    </Box>
  );
};
